import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ChartOrderItem } from '../../utils/types/analytics/analytics_types';
import { DashletIconGroup } from './DashletIconGroup';

type NumericalDashletProps = {
  id: string;
  name: string;
  value: number;
  currency?: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const NumericalDashlet = ({
  id,
  name,
  value,
  currency,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: NumericalDashletProps) => {
  const formattedValue = useMemo(() => {
    const roundedValue = Math.round(value);
    let formatted = roundedValue.toString();
    if (roundedValue >= 1000000) {
      formatted = parseFloat((roundedValue / 1000000).toFixed(1)) + 'M';
    } else if (roundedValue >= 1000) {
      formatted = parseFloat((roundedValue / 1000).toFixed(1)) + 'K';
    }
    return formatted;
  }, [value]);

  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: 180,
        borderRadius: '16px',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '2.25rem' : '0.875rem',
            color: 'text.secondary',
            mb: fullScreenDetected ? 3 : 1.5,
            textAlign: 'center',
          }}
        >
          {currency ? currency : 'Hours'} Saved - {name}
        </Typography>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: fullScreenDetected
              ? '6rem'
              : formattedValue.length > 3
                ? '2.75rem'
                : '3.75rem',
            color: 'primary.main',
            letterSpacing: '-0.03em',
            lineHeight: 1,
            textAlign: 'center',
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    </Paper>
  );
};
